import { keepPreviousData, useQuery, type UseQueryResult } from "@tanstack/react-query";
import type { components } from "@oathera/contract/api";
import { apiFetch, type ApiError } from "../../lib/api.ts";
import { useTimeRange } from "../../state/time-range.tsx";

/**
 * The reads for the Posture screen (task 11.10; design 11, 12.2 Posture row).
 *
 * TWO operator-API operations back the screen, both role `viewer`, taken straight from the generated
 * `@oathera/contract/api` and never redefined here:
 *
 * - `GET /api/v1/posture/findings?from=…&to=…` → {@link PostureFindingPage}: NHI and host findings
 *   with, per contract, `items` (one {@link PostureFinding} each — id, kind, code, risk_tier, agent,
 *   source_id, first_seen, last_seen) AND a `by_code_and_tier` array of {code, risk_tier, count}
 *   aggregates. The screen's "NHI findings by code and tier" breakdown reads `by_code_and_tier`
 *   directly (it is the authoritative, server-built two-dimensional count); the raw `items` back the
 *   finding list. This response carries NO trend series.
 * - `GET /api/v1/posture/drift?from=…&to=…` → {@link PostureDriftPage}: host configuration drift
 *   entries (one {@link PostureDriftEntry} each — id, ts, change_kind, host_confidence,
 *   host_attestation, source_id, event_id) AND, unlike findings, a `trend` array of {t, count}
 *   points. That `trend` is the ONLY time-series the posture contract provides, so it is the sole
 *   honest source for the screen's trend chart (see {@link PostureDriftTrendPoint}); the findings
 *   endpoint has no series, so a findings-over-time trend is surfaced as an honest "unknown" rather
 *   than fabricated from the point-in-time `by_code_and_tier` counts (design 12.3).
 *
 * BOTH are WINDOWED reads (they take `from`/`to`), so — exactly like the Overview/Decisions/Identity/
 * Enforcement screens — the window is resolved from the shared time-range and pausing freezes the
 * query: in live mode "now" is bucketed to a 10s grid to keep the query key stable between ticks;
 * when frozen (paused or custom) `resolveWindow` returns the exact frozen window and the key stops
 * advancing (design 12.2, FR-13 AC2). They are REST snapshots over the window (not live-stream
 * views) because the by-code-and-tier breakdown and the drift trend are server-built aggregates the
 * stream does not carry.
 *
 * Fetches go through `lib/api.ts`, so a cold-core 503 surfaces as a `waking` {@link ApiError} and
 * the shell shows "Waking the control plane"; queries are retried per `query-client.ts`.
 */

/** The contract `PostureFindingPage` response body (findings + by-code-and-tier counts). */
export type PostureFindingPage = components["schemas"]["PostureFindingPage"];
/** A single NHI/host finding row. */
export type PostureFinding = components["schemas"]["PostureFinding"];
/** A single by-code-and-tier aggregate row (code + risk tier + count). */
export type PostureByCodeAndTier = PostureFindingPage["by_code_and_tier"][number];
/** The contract `PostureDriftPage` response body (drift entries + trend series). */
export type PostureDriftPage = components["schemas"]["PostureDriftPage"];
/** A single host-drift entry row. */
export type PostureDriftEntry = PostureDriftPage["items"][number];
/** A single trend point (timestamp + count) — the ONLY time-series the posture contract provides. */
export type PostureDriftTrendPoint = PostureDriftPage["trend"][number];

/** The resolved time window a query ran for, so the screen can label the snapshot honestly. */
export interface PostureWindow {
  readonly fromMs: number;
  readonly toMs: number;
}

export interface UsePostureFindingsResult {
  readonly query: UseQueryResult<PostureFindingPage, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: PostureWindow;
}

export interface UsePostureDriftResult {
  readonly query: UseQueryResult<PostureDriftPage, ApiError>;
  /** The window the current query key was built from (what the data describes). */
  readonly window: PostureWindow;
}

/** Resolve the current window from the time-range (live buckets to 10s; frozen is exact). */
function useResolvedWindow(): PostureWindow {
  const timeRange = useTimeRange();
  const nowMs = timeRange.live ? bucket(Date.now(), 10_000) : Date.now();
  const resolved = timeRange.resolveWindow(nowMs);
  return { fromMs: resolved.fromMs, toMs: resolved.toMs };
}

/**
 * Fetch the posture findings page for the current window. The query key carries the window so a
 * frozen window never refetches and a live window advances on the 10s grid; `staleTime` /
 * `refetchInterval` match the other screens (live tolerates a little staleness, frozen never goes
 * stale). The first page only — `next_cursor` is left for a later pagination affordance, matching
 * the integrity/identity screens.
 */
export function usePostureFindings(): UsePostureFindingsResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<PostureFindingPage, ApiError>({
    queryKey: ["posture-findings", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<PostureFindingPage>("/posture/findings", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/**
 * Fetch the host-drift page (entries + trend) for the current window. Same windowing and polling as
 * {@link usePostureFindings}. The `trend` on this response is the screen's only time-series source.
 */
export function usePostureDrift(): UsePostureDriftResult {
  const timeRange = useTimeRange();
  const window = useResolvedWindow();

  const query = useQuery<PostureDriftPage, ApiError>({
    queryKey: ["posture-drift", window.fromMs, window.toMs],
    queryFn: ({ signal }) =>
      apiFetch<PostureDriftPage>("/posture/drift", {
        query: { from: window.fromMs, to: window.toMs },
        signal,
      }),
    staleTime: timeRange.live ? 10_000 : Infinity,
    refetchInterval: timeRange.live ? 15_000 : false,
    // Keep the previous window's data while the next live window fetches, so the screen does
    // not blink through its loading state on each ~10s tick (seamless live refresh).
    placeholderData: keepPreviousData,
  });

  return { query, window };
}

/** Floor `value` to the nearest multiple of `size` (keeps the live query key stable). */
function bucket(value: number, size: number): number {
  return Math.floor(value / size) * size;
}
